<script setup>
import { defineModel, ref, computed, defineEmits, defineProps, onMounted } from 'vue'
import GoogleSheetsLogoSvg from "@/components/UI/svg/GoogleSheetsLogoSvg.vue";
import NewTabSvg from "@/components/UI/svg/NewTabSvg.vue";
import LoadingComponent from "@/components/UI/LoadingComponent.vue";
import RefreshSvg from "@/components/UI/svg/RefreshSvg.vue";
import SelectTabDropdown from "@/components/Connection/SelectTabDropdown.vue";
import {isEmpty, isStringEmpty} from "@/service/utils";
import TrashCanSvg from "@/components/UI/svg/TrashCanSvg.vue";
import TickBlueSvg from "@/components/UI/svg/TickBlueSvg.vue";
import DropdownArrowSvg from "@/components/UI/svg/DropdownArrowSvg.vue";
import CheckBox from "@/components/UI/CheckBox.vue";
import GooglePicker from "@/components/GooglePicker.vue";
import CrossCloseNotifySvg from "@/components/UI/svg/CrossCloseNotifySvg.vue";


const file = defineModel("file")


const emits = defineEmits(['reload-tabs', 'open-modal', 'delete-item', 'save-item', 'save-files'])

const isSettingsVisible = ref(false)

const props = defineProps({
    isSource: {
        type: Boolean,
        default: true
    }
})

onMounted(async () => {

})

const isFileEmpty = computed(() => {
    return file.value?.name === undefined
})

const sourceLabel = "Выгружать заголовок таблицы"
const targetLabel = "Сохранять исторические данные"


function reloadTabs() {
    emits("reload-tabs", file.value.id, file.value.tab)
}

function handleFileSelection(files) {
    emits('save-files', files)

}

function toggleSettingsVisible() {
    isSettingsVisible.value = !isSettingsVisible.value
}

function deleteItem() {
    emits('delete-item', file.value.id)
}

function saveItem() {
    emits('save-item', file.value.id)
}

</script>

<template>
    <div class="w-4/5 bg-blue-50 rounded-lg">
        <!-- Поле для выбора таблицы из которой пойдет выгрузка -->
        <div class="flex w-full items-center h-full justify-between flex-col">
            <div class="flex w-full h-full justify-start items-center px-4 py-10 lg:px-20">
                <div class="flex-col items-center justify-center hidden lg:flex">
                    <div class="size-6">
                        <GoogleSheetsLogoSvg class="size-6" />
                    </div>
                </div>
                <div
                    class="w-full h-12 border border-gray-400 cursor-pointer hover:border-gray-700 relative lg:ml-6 lg:rounded-md lg:h-14"
                >
                    <div class="absolute -top-2 left-10 bg-blue-50 text-xs px-2">
                        Файл
                    </div>
                    <div class="flex items-center justify-between w-full h-full">
                        <p v-if="isFileEmpty" class="text-gray-500 px-4 select-none block overflow-hidden whitespace-nowrap overflow-ellipsis">
                            Файл не выбран
                        </p>
                        <p v-else class="text-black px-4 select-none">
                            {{ file?.name }}
                        </p>
                        <GooglePicker
                            :multiple="false"
                            button-text="Выбрать файлы из Google Диска"
                            @file-selected="handleFileSelection"
                            class="px-4"
                        >
                            <template #button-content>
                                <span>📁 Выбрать файлы</span>
                            </template>
                        </GooglePicker>
                    </div>
                </div>
                <div v-on:click="deleteItem" class="rounded-full size-10 hover:bg-gray-200 flex items-center justify-center mx-4 cursor-pointer transition-all duration-150">
                    <CrossCloseNotifySvg class="size-6"/>
                </div>
            </div>
            <div class="flex w-full h-full mx-auto" v-if="!isStringEmpty(file.id)">
                <div class="flex w-full h-full items-center justify-start px-4 pb-10 lg:px-20">
                    <div class="flex-col items-center justify-center hidden lg:flex">
                        <NewTabSvg class="size-6" />
                    </div>
                    <div class="flex w-full h-12 lg:h-14 items-center justify-center">
                        <SelectTabDropdown
                            v-model:selected-tab="file.tab"
                            :isLoading="file.isLoading"
                            :tabs="file?.tabs"
                            class="w-full h-full lg:ml-6"
                        />
                        <div class="flex mx-4 justify-center items-center cursor-pointer hover:bg-gray-200 rounded-full transition-all duration-200 size-8 lg:size-10">
                            <RefreshSvg
                                v-if="!file.isLoading"
                                v-on:click="reloadTabs"
                                class="size-6"
                            />
                            <LoadingComponent
                                v-else
                                class="size-6"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="w-full">
                <div
                    v-if="!isStringEmpty(file.id)"
                    class="mx-auto my-5 h-12 w-[calc(100%-40px)] rounded-lg hover:bg-blue-100 flex items-center justify-between cursor-pointer"
                    v-on:click="toggleSettingsVisible()"
                >
                    <div class="font-bold text-sm mx-5">
                        Расширенные настройки
                    </div>
                    <DropdownArrowSvg
                        class="size-6 mr-5"
                        :class="{
                        'rotate-180': isSettingsVisible
                    }"
                    />
                </div>
                <div
                    class="mx-10 w-full flex"
                    v-show="isSettingsVisible"
                >
                    <div class="flex items-center ml-20" v-if="props.isSource">
                        <CheckBox v-model="file.include_header"/>
                        <div class="text-sm font-bold"> {{ props.isSource ? sourceLabel : targetLabel }} </div>
                    </div>
                    <div class="flex items-center ml-20" v-else>
                        <CheckBox v-model="file.append"/>
                        <div class="text-sm font-bold"> {{ props.isSource ? sourceLabel : targetLabel }} </div>
                    </div>
                </div>
            </div>

            <div class="w-full flex" v-if="!isStringEmpty(file.id) && !isEmpty(file.tab)">
                <div class="ml-auto mr-10 flex items-center gap-2 m-2">
                    <div class="flex items-center gap-2 px-4 py-2 cursor-pointer rounded-md transition-all duration-150 hover:bg-gray-200" v-on:click="deleteItem">
                        <TrashCanSvg class="size-5 stroke-2"/>
                        <div class="font-bold text-gray-600">
                            Удалить
                        </div>
                    </div>
                    <div class="flex items-center gap-1 px-4 py-2 cursor-pointer rounded-md transition-all duration-150 hover:bg-blue-100" v-on:click="saveItem">
                        <TickBlueSvg class="size-5 stroke-2"/>
                        <div class="font-bold text-blue-500">
                            Сохранить
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>